/**
 * ArollProjects.tsx — the project dashboard: list, folders, first-run
 * welcome.
 *
 * NAMING (Nick, 2026-10-05): the user-facing noun is PROJECT, in the
 * app, on the site, in pricing and in server messages. It was ASSET
 * from 2026-08-05 (#1075) until the consistency audit found the app
 * saying "asset" while the landing page, the plans ("Unlimited
 * projects") and the Free-plan limit said "project". Each one holds an
 * interview, several cuts, rushes and a client link, which "asset"
 * (one file) undersold. All twelve dictionaries were restored from
 * their pre-#1075 wording, not re-translated.
 * If a value here says "asset", it is a bug.
 */
export const projects = {
  /** Header action that opens the inline new-folder input. */
  'projects.addFolder': 'Add folder',
  /** Primary header button. Creates a Playback project from a rendered
   *  cut (one project, one module: 2026-10-21). */
  'projects.newProject': 'New Playback',
  /** Button label while a project is being created (present progressive, no ellipsis). */
  'projects.creating': 'Creating',
  /** Search input placeholder. */
  'projects.searchPlaceholder': 'Search projects',
  /** Shown when the asset list failed to load. */
  'projects.loadError': "Couldn't load projects. Refresh to try again.",
  /** Shown while the list is loading. */
  'projects.loadingProjects': 'Loading projects…',
  /** Empty state when a search matches nothing. */
  'projects.noSearchMatches': 'Nothing matches that search.',
  /** Empty state for workspace customers (an "interview" is a filmed sit-down interview). */
  'projects.emptyTenant': 'No projects yet. Create one and drop in an interview.',
  /** Default name given to a freshly created asset (user renames it later). */
  'projects.untitledProject': 'Untitled project',

  // Asset rows
  /** aria-label of the inline rename input on an asset row. */
  'projects.projectNameAria': 'Project name',
  /** Pill on assets whose cut the client has approved. */
  'projects.approvedBadge': 'Approved',
  /** Row/folder action: start an inline rename. */
  'projects.rename': 'Rename',
  /** Row action: open the move-to-folder picker. */
  'projects.move': 'Move',
  /** Row/folder action: first click of the two-step delete. */
  'projects.delete': 'Delete',
  /** Second click of the two-step delete — a short "are you sure" in button form. */
  'projects.deleteConfirm': 'Sure? Delete',
  /** Move picker when the workspace has no folders. */
  'projects.noFoldersYet': 'No folders yet',
  /** Move picker option that files the asset back at the root level. */
  'projects.removeFromFolder': 'Remove from folder',
  /** Toast after deleting an asset. */
  'projects.projectDeleted': 'Project deleted.',

  // Version stacking (Playback world only): drag an asset card onto
  // another to fold it in, Frame.io-style.
  /** Overlay on the card under a dragged asset, mid-drag. */
  'projects.stackHere': 'Stack versions here',
  /** Confirm-bar copy before stacking. {source} and {target} are asset names. */
  'projects.stackConfirm':
    'Stack "{source}" onto "{target}"? Its cuts become new versions of "{target}" with their reviews attached, and the separate project goes away. The share link that keeps working is "{target}"’s.',
  /** Confirm-bar button. */
  'projects.stackCta': 'Stack versions',
  /** Toast after stacking. {n} cuts moved; {target} is the asset name. */
  'projects.stacked_one': '{n} cut stacked onto "{target}".',
  'projects.stacked_other': '{n} cuts stacked onto "{target}".',

  // Folders
  /** Placeholder of the new-folder / rename-folder input. */
  'projects.folderNamePlaceholder': 'Folder name',
  /** aria-label of the new-folder input. */
  'projects.newFolderNameAria': 'New folder name',
  /** aria-label of the rename-folder input. */
  'projects.folderNameAria': 'Folder name',
  /** Hover title on a folder row explaining the three interactions. */
  'projects.folderRowTitle':
    'Click to open. Double-click to rename. Drop a project here to file it.',
  /** Folder-row action creating an asset inside that folder. Keep the leading "+". */
  'projects.addProject': '+ Project',
  /** Shown inside an open folder with no assets. "+ Asset" refers to projects.addProject. */
  'projects.folderEmptyHint': 'Empty. Drag a project in, or use + Project.',
  /** Drag-target shown mid-drag; dropping moves the asset out of its folder. */
  'projects.dropToRoot': 'Drop here to move out of its folder',
  /** Toast after deleting an empty folder. */
  'projects.folderRemoved': 'Folder removed.',
  /** Toast after deleting a folder whose assets were re-homed to the root
   *  ("All") list. {n} is the asset count. */
  'projects.folderRemovedMoved_one': 'Folder removed. {n} project moved to All.',
  'projects.folderRemovedMoved_other': 'Folder removed. {n} projects moved to All.',

  // Folder tiles (Option B layout, Nick 2026-07-21)
  /** Section label above the folder tiles. */
  'projects.foldersLabel': 'Folders',
  /** Section label above the loose (root-level) asset rows. */
  'projects.looseLabel': 'Projects',
  /** The dashed create-a-folder tile. */
  'projects.newFolderTile': 'New folder',
  /** Breadcrumb back to the root view from inside a folder. */
  'projects.backToAll': 'All projects',
  /** Asset count on a folder tile. {n} is the count. */
  'projects.projectCount_one': '{n} project',
  'projects.projectCount_other': '{n} projects',
  /** Hover title on a folder tile. */
  'projects.folderTileTitle': 'Open the folder. Drop a project here to file it.',

  // Asset cards (an asset = one transcript; the card leads with the
  // footage: thumbnail, runtime, drafts, ingest state).
  /** Draft count on an asset card. {n} is the count. */
  'projects.draftCount_one': '{n} cut',
  'projects.draftCount_other': '{n} cuts',
  /** Card meta when the transcript is ready but no drafts exist yet. */
  'projects.noDraftsYet': 'No cuts yet',
  /** Card state chips over the thumbnail. */
  'projects.cardPreparing': 'Preparing',
  'projects.cardTranscribing': 'Transcribing',
  'projects.cardFailed': 'Failed',
  /** Card placeholder when the asset has no footage uploaded yet. */
  'projects.noFootage': 'No footage yet',
  'projects.noCut': 'No cut yet',
  'projects.uploading': 'Uploading',
  'projects.cutCount_one': '{n} cut',
  'projects.cutCount_other': '{n} cuts',
  /** Card state when the plan's retention window removed the video. */
  'projects.mediaExpired': 'Video expired',

  // First-run welcome (empty workspace hero)
  /** Small uppercase kicker above the welcome headline. */
  'projects.welcomeKicker': 'Your workspace is ready',
  /** Welcome headline, line 1 ("cut" = an edited video, the thing a client reviews). */
  'projects.welcomeHeadline': 'Share a cut.',
  /** Welcome headline, line 2 ("sign-off" = the client's approval). */
  'projects.welcomeHeadlineSub': 'Get the sign-off.',
  /** Welcome body copy. "A-Roll" is a feature name — do not translate it. */
  'projects.welcomeBody': 'Upload a cut and send one link. Your client leaves notes on the exact frame and signs off in writing. Starting from interview footage? A-Roll transcribes it and drafts the cut with you.',
  /** Welcome CTA button. */
  'projects.createFirstProject': 'Upload your first cut',
  /** Four-step explainer under the welcome CTA — the review loop:
   *  upload a cut, share one link, notes on the frame, sign-off. */
  'projects.step1Title': 'Upload',
  'projects.step1Body': 'Drop in a rendered cut. Each new version stacks on the last.',
  'projects.step2Title': 'Share',
  'projects.step2Body': 'One link for the client. No account, no seat to pay for.',
  'projects.step3Title': 'Notes',
  'projects.step3Body': 'Feedback lands on the exact frame, and exports as markers for your NLE.',
  'projects.step4Title': 'Sign-off',
  'projects.step4Body': 'Approval in writing, on the cut itself, with a name and a time.',

  /* Ballast welcome — the door at bare /aroll. Each door carries its
     module's landing hero mark and its world's most recent projects
     (Nick, 2026-08-06). */
  /** Greeting above the two doors. {name} is the person's first name. */
  'welcome.greeting': 'Welcome back, {name}',
  /** Same greeting when we don't know the name yet. */
  'welcome.greetingAnon': 'Welcome back',
  /** One line under the greeting. Says what the two doors are. */
  'welcome.subtitle': 'Two workspaces. Pick the one you want to work in.',
  /** A-Roll door: what that world is for. Shown while the world has no
   *  projects to tease. */
  'welcome.arollBody': 'Turn raw interview footage into a cut.',
  /** Playback door: what that world is for. Shown while the world has
   *  no projects to tease. */
  'welcome.playbackBody': 'Send a cut for review and collect frame-accurate notes.',
  /** Verb on both doors. */
  'welcome.enter': 'Open',
  /** Tiny label over each door's most-recent-projects list. */
  'welcome.recent': 'Recent',
  /** The production-line graphic's two phase captions, under the
   *  divider where KG's side hands off to the client's. Keep them to
   *  one short word each — they sit inside the drawing. */
  'welcome.lineCut': 'CUT',
  'welcome.lineReview': 'REVIEW',
  /** Second create action: an A-Roll project, from long interview
   *  footage rather than a finished cut. */
  'projects.startFromAroll': 'New A-Roll',
  /** Card and header pill: a Playback project (cuts for client
   *  feedback). Product name, the same in every language. */
  'projects.typeReview':
    'Playback',
  /** Legacy card pill, kept for older builds; reads as the module. */
  'projects.typeArollJob':
    'A-Roll',
  /** Module filter on the projects page: All / A-Roll / Playback. */
  'projects.filterAll': 'All',
  /** Screen-reader name of the module filter. */
  'projects.filterLabel': 'Show',
  /** The filter hides every project in this folder. */
  'projects.noneOfModule': 'Nothing of that kind here.',
} as const;
